import React, { useContext, useEffect, useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import "rsuite/dist/rsuite.min.css";
import StarekContext from "../../context/StarekContext";

const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = pad(dt.getMonth() + 1);
  const dd = pad(dt.getDate());
  const hh = pad(dt.getHours());
  const min = pad(dt.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const normalizeToLocalInput = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val).slice(0, 16) : toLocalInput(d);
};

const MaintenanceOffCanvasSidebar = ({
  isOpen,
  onClose,
  onCreated = () => {},
  devices: devicesProp = null,
  type = "add",
  schedule = null,
  customCreate,
  customUpdate,
}) => {
  const starekContext = useContext(StarekContext);
  const lastUsedPlant = starekContext?.lastUsedPlant;
  const lastUsedWorkspace = starekContext?.lastUsedWorkspace;
  const token = starekContext?.auth;
  const { host, scheme, port } = lastUsedWorkspace?.engine || {};
  const apiUrl =
    lastUsedWorkspace && scheme && host && port
      ? `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`
      : null;

  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState(devicesProp || []);
  const [error, setError] = useState(null);

  const nowLocal = useMemo(() => toLocalInput(new Date()), []);
  const [deviceId, setDeviceId] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");

  useEffect(() => {
    if (type === "edit" && schedule) {
      let attrs = schedule?.schedule_attributes;
      if (typeof attrs === "string") {
        try {
          attrs = JSON.parse(attrs);
        } catch {
          attrs = {};
        }
      }
      const from = attrs?.from ? new Date(attrs.from) : new Date();
      const to = attrs?.to ? new Date(attrs.to) : from;
      setDeviceId(schedule?.device_id ?? "");
      setStartLocal(toLocalInput(from));
      setEndLocal(toLocalInput(to));
    } else {
      const now = new Date();
      setDeviceId("");
      setStartLocal(toLocalInput(now));
      setEndLocal(toLocalInput(now));
    }
    setError(null);
  }, [isOpen, type, schedule]);

  useEffect(() => {
    if (devicesProp?.length) return;
    if (!isOpen || !apiUrl || !token || !lastUsedPlant?.id) return;

    const fetchDevices = async () => {
      setLoading(true);
      try {
        const q1 = {
          query: `
          { shadowDevices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) { id name } }
        `,
        };
        let res = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(q1),
        });
        let json = await res.json();
        let list = json?.data?.shadowDevices || [];

        if (!Array.isArray(list) || list.length === 0) {
          const q2 = {
            query: `
            { devices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) { id name } }
          `,
          };
          res = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(q2),
          });
          json = await res.json();
          list = json?.data?.devices || [];
        }
        setDevices(list);
      } catch (e) {
        console.error("Error fetching devices:", e);
        setDevices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, [isOpen, apiUrl, token, lastUsedPlant?.id, devicesProp]);

  const validate = () => {
    if (!deviceId) return "Please select a device.";
    if (!startLocal || !endLocal) return "Please select both Start and End.";
    const s = new Date(startLocal);
    const e = new Date(endLocal);
    const now = new Date();
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return "Invalid date/time.";
    if (s <= now) return "Start time must be in the future.";
    if (e <= s) return "End time must be after Start time.";
    return null;
  };

  const createViaGraphQL = async ({ deviceId, from, to }) => {
    if (!apiUrl) throw new Error("API not configured.");
    const uid = starekContext?.user?.id;
    const q = {
      query: `
        mutation CreateOneMaintenanceSchedule {
          createOneMaintenanceSchedule(data: {
            schedule_type: 2
            name: "Scheduled Maintenance"
            schedule_attributes: "{\\"from\\": \\"${from.toISOString()}\\", \\"to\\": \\"${to.toISOString()}\\"}"
            plant: { connect: { id: ${lastUsedPlant.id} } }
            device: { connect: { id: ${JSON.stringify(deviceId)} } }
            created_by: { connect: { id: ${uid} } }
            updated_by: { connect: { id: ${uid} } }
          }) { id }
        }
      `,
    };
    const r = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(q),
    });
    const j = await r.json();
    if (!j?.data?.createOneMaintenanceSchedule?.id) {
      throw new Error(j?.errors?.[0]?.message || "Failed to create schedule");
    }
  };

  const updateViaGraphQL = async ({ id, deviceId, from, to }) => {
    if (!apiUrl) throw new Error("API not configured.");
    const uid = starekContext?.user?.id;
    const q = {
      query: `
        mutation UpdateOneMaintenanceSchedule {
          updateOneMaintenanceSchedule(
            where: { id: ${id} }
            data: {
              device: { connect: { id: ${JSON.stringify(deviceId)} } }
              schedule_attributes: { set: "{\\"from\\": \\"${from.toISOString()}\\", \\"to\\": \\"${to.toISOString()}\\"}" }
              updated_by: { connect: { id: ${uid} } }
            }
          ) { id }
        }
      `,
    };
    const r = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(q),
    });
    const j = await r.json();
    if (!j?.data?.updateOneMaintenanceSchedule?.id) {
      throw new Error(j?.errors?.[0]?.message || "Failed to update schedule");
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const err = validate();
    if (err) return setError(err);

    const from = new Date(startLocal);
    const to = new Date(endLocal);
    const payload = { id: schedule?.id, deviceId, from, to };

    try {
      setLoading(true);
      if (type === "edit") {
        if (customUpdate) await customUpdate(payload);
        else await updateViaGraphQL(payload);
      } else {
        if (customCreate) await customCreate(payload);
        else await createViaGraphQL(payload);
      }
      await onCreated?.();
      onClose();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Offcanvas
      show={isOpen}
      onHide={onClose}
      placement="end"
      backdrop={true}
      style={{ width: "580px", overflow: "visible", zIndex: 1050 }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <h2 className="tw-text-lg tw-font-semibold">
            {type === "edit" ? "Edit Maintenance" : "Add Maintenance"}
          </h2>
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="tw-flex tw-flex-col tw-h-full tw-overflow-visible tw-relative">
        <div className="tw-flex-grow tw-flex tw-flex-col tw-gap-4 tw-overflow-auto">
          <div>
            <label className="tw-text-sm tw-font-medium">Select Device</label>
            <select
              className="tw-w-full tw-border tw-rounded-md tw-p-2"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select device</option>
              {devices?.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.id}
                </option>
              ))}
            </select>
          </div>

          <div className="tw-flex tw-flex-col">
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1">
              Start time
            </label>
            <input
              type="datetime-local"
              className="tw-border tw-rounded tw-px-3 tw-py-2"
              value={normalizeToLocalInput(startLocal)}
              onChange={(e) => setStartLocal(e.target.value)}
              disabled={loading}
              min={nowLocal}
            />
          </div>

          <div className="tw-flex tw-flex-col">
            <label className="tw-text-sm tw-text-gray-600 tw-mb-1">
              End time
            </label>
            <input
              type="datetime-local"
              className="tw-border tw-rounded tw-px-3 tw-py-2"
              value={normalizeToLocalInput(endLocal)}
              onChange={(e) => setEndLocal(e.target.value)}
              disabled={loading}
              min={startLocal || nowLocal}
            />
          </div>
        </div>

        {error && <div className="tw-text-red-600 tw-mt-2">{error}</div>}

        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
          <button
            className="tw-px-4 tw-py-2 tw-text-gray-600 tw-bg-gray-200 tw-rounded-md"
            onClick={onClose}
            disabled={loading}
          >
            Discard
          </button>
          <button
            className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md tw-inline-flex tw-items-center tw-gap-2 disabled:tw-opacity-60"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading && (
              <svg
                className="tw-animate-spin tw-h-4 tw-w-4 tw-text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="tw-opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="tw-opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            )}
            {type === "edit" ? "Update" : "Add"}
          </button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default MaintenanceOffCanvasSidebar;
